import { VIDEO_QUALITY_PROFILES, VideoQualityProfile } from "@app/shared/src/helpers/videoProfiles";
import { ChirpConfig, Device, FleetDefaults, RadarConfiguration, TrackerConfig, TrackerConfigType } from "@app/shared/src/models";

/**
 * Radar and video, as one comparable value.
 *
 * A section shows as inherited while it still matches the organization's stated defaults, so the
 * comparison has to cover every field the section can edit — not a sample of them. Reading the
 * device into a flat record and comparing that record is what makes "all of it, or it is an
 * override" checkable rather than a promise, and it is why the compared field list and the edited
 * field list are the same list.
 *
 * This section is the simple case: every value lives on the device row, so a save is one call.
 * Alerts and notifications reach into triggers, zones and endpoint joins instead.
 */
export type RadarVideoSettings = {
    /** Undefined when the unit has no sensor config, so there is nothing to read or write. */
    radarEnabled?: boolean;
    chirpConfigId?: string;
    radarTrackerConfigId?: string;
    videoTrackerConfigId?: string;
    /**
     * Hand-authored tracker JSON, present only while the unit runs one (no preset id). A custom is
     * a deliberate per-unit setting — the opposite of "nothing chosen" — so it travels on the
     * record where the editors and applyRadarVideo can see it, but never appears on a fleet record,
     * which keeps it out of every keys-of-the-defaults comparison.
     */
    radarTrackerCustomConfig?: string;
    videoTrackerCustomConfig?: string;

    dvrRecordingEnabled: boolean;
    dvrResolutionX: number;
    dvrResolutionY: number;
    dvrFps: number;
    dvrBitrateBps: number;
    dvrRetentionHours: number;

    streamResolutionX: number;
    streamResolutionY: number;
    streamFps: number;
    streamBitrateBps: number;
};

const radarOf = (device: Device): RadarConfiguration | undefined => device.sensorConfig as RadarConfiguration | undefined;

/** Old rows wrote "no preset" as the zero guid rather than null; both mean nothing is chosen. */
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const presetIdOf = (id: string | null | undefined): string | undefined => (!id || id === EMPTY_GUID ? undefined : id);

/** What this truck is actually running. */
export function readRadarVideo(device: Device): RadarVideoSettings {
    const radar = radarOf(device);
    const radarTrackerId = presetIdOf(radar?.radarTrackerConfigId);
    const videoTrackerId = presetIdOf(radar?.videoTrackerConfigId);
    return {
        radarEnabled: radar?.transmitterEnabled,
        chirpConfigId: presetIdOf(radar?.chirpConfigId),
        radarTrackerConfigId: radarTrackerId,
        videoTrackerConfigId: videoTrackerId,
        // A tracker is custom when JSON is inlined with no preset id behind it (the dashboard's
        // rule). With an id, the JSON is just that preset's copy — not a custom.
        ...(radarTrackerId === undefined && radar?.radarTrackerConfig
            ? { radarTrackerCustomConfig: radar.radarTrackerConfig }
            : {}),
        ...(videoTrackerId === undefined && radar?.videoTrackerConfig
            ? { videoTrackerCustomConfig: radar.videoTrackerConfig }
            : {}),

        dvrRecordingEnabled: device.enableDvrRecording,
        dvrResolutionX: device.dvrResolutionX,
        dvrResolutionY: device.dvrResolutionY,
        dvrFps: device.dvrFps,
        dvrBitrateBps: device.dvrBitrateBps,
        dvrRetentionHours: device.dvrRetentionHours,

        streamResolutionX: device.videoResolutionX,
        streamResolutionY: device.videoResolutionY,
        streamFps: device.videoFps,
        streamBitrateBps: device.videoBitrateBps,
    };
}

/** What the fleet says this truck should be running. */
export function radarVideoFromDefaults(defaults: FleetDefaults): RadarVideoSettings {
    return {
        radarEnabled: defaults.radarEnabled,
        chirpConfigId: defaults.chirpConfigId ?? undefined,
        radarTrackerConfigId: defaults.radarTrackerConfigId ?? undefined,
        videoTrackerConfigId: defaults.videoTrackerConfigId ?? undefined,

        dvrRecordingEnabled: defaults.dvrRecordingEnabled,
        dvrResolutionX: defaults.dvrResolutionX,
        dvrResolutionY: defaults.dvrResolutionY,
        dvrFps: defaults.dvrFps,
        dvrBitrateBps: defaults.dvrBitrateBps,
        dvrRetentionHours: defaults.dvrRetentionHours,

        streamResolutionX: defaults.streamResolutionX,
        streamResolutionY: defaults.streamResolutionY,
        streamFps: defaults.streamFps,
        streamBitrateBps: defaults.streamBitrateBps,
    };
}

/**
 * Fields both sides can actually speak to, so a truck cannot be "overridden" on the rest.
 *
 * A null preset id means no director has chosen one fleet-wide yet, not "none" — every truck keeps
 * whatever it was commissioned with. Comparing those would report every truck as overridden on day
 * one, which would make the badge meaningless before anyone had touched anything.
 *
 * The same holds from the truck's side: a unit with no sensor config reads every radar field as
 * undefined, and counting those as drift pinned an override no reset could clear.
 */
function comparableKeys(
    current: RadarVideoSettings,
    defaults: RadarVideoSettings,
): (keyof RadarVideoSettings)[] {
    const keys = Object.keys(defaults) as (keyof RadarVideoSettings)[];
    return keys.filter((key) => defaults[key] !== undefined && current[key] !== undefined);
}

/** Whether this truck still follows the fleet on every field this section can edit. */
export function radarVideoMatches(current: RadarVideoSettings, defaults: RadarVideoSettings): boolean {
    return comparableKeys(current, defaults).every((key) => current[key] === defaults[key]);
}

/** The fields that differ, for telling a director what "overridden" actually means on this truck. */
export function radarVideoDifferences(
    current: RadarVideoSettings,
    defaults: RadarVideoSettings,
): (keyof RadarVideoSettings)[] {
    return comparableKeys(current, defaults).filter((key) => current[key] !== defaults[key]);
}

/** The preset catalogues, for inlining a chosen preset's JSON the way the dashboard's editor does. */
export type RadarPresets = { chirpConfigs?: ChirpConfig[]; trackerConfigs?: TrackerConfig[] };

/**
 * Write settings onto the device instance, in place.
 *
 * Mutating rather than rebuilding from a spread, matching how the dashboard's own editors write:
 * the generated client serialises class shape (Date objects, the `$type` discriminator on the
 * nested sensor config), and a plain object literal loses all of it. The caller saves the same
 * instance it passed in.
 *
 * Pass the preset catalogues wherever they are in hand: a chosen preset's JSON is inlined next to
 * its id, because the JSON is what the unit runs. The server re-resolves tracker ids when serving
 * settings, but not the chirp id — a chirp id written without its JSON never re-tunes the radar.
 */
export function applyRadarVideo(device: Device, settings: RadarVideoSettings, presets: RadarPresets = {}): void {
    const radar = radarOf(device);
    if (radar) {
        if (settings.radarEnabled !== undefined) radar.transmitterEnabled = settings.radarEnabled;
        // Only move a preset that is actually chosen; an undefined id means "leave this truck
        // alone" — unless a custom config is in hand, which is itself the setting.
        //
        // With a catalogue in hand, an id it does not contain is a stale or ghost selection:
        // writing it without its JSON would let id and JSON diverge on the row, so the truck is
        // left as it is. With no catalogue passed, the id-only write stands (the read/apply round
        // trip; the server re-resolves tracker ids at serve time).
        if (settings.chirpConfigId !== undefined) {
            const chirp = presets.chirpConfigs?.find((c) => c.id === settings.chirpConfigId);
            if (chirp || !presets.chirpConfigs) {
                radar.chirpConfigId = settings.chirpConfigId;
                if (chirp) radar.chirpConfig = chirp.chirpConfig ?? "";
            }
        }
        if (settings.radarTrackerConfigId !== undefined) {
            const preset = presets.trackerConfigs?.find((t) => t.id === settings.radarTrackerConfigId);
            if (preset || !presets.trackerConfigs) {
                radar.radarTrackerConfigId = settings.radarTrackerConfigId;
                if (preset) radar.radarTrackerConfig = preset.trackerConfig ?? "";
            }
        } else if (settings.radarTrackerCustomConfig !== undefined) {
            radar.radarTrackerConfigId = undefined;
            radar.radarTrackerConfig = settings.radarTrackerCustomConfig;
        }
        if (settings.videoTrackerConfigId !== undefined) {
            const preset = presets.trackerConfigs?.find((t) => t.id === settings.videoTrackerConfigId);
            if (preset || !presets.trackerConfigs) {
                radar.videoTrackerConfigId = settings.videoTrackerConfigId;
                if (preset) radar.videoTrackerConfig = preset.trackerConfig ?? "";
            }
        } else if (settings.videoTrackerCustomConfig !== undefined) {
            radar.videoTrackerConfigId = undefined;
            radar.videoTrackerConfig = settings.videoTrackerCustomConfig;
        }
    }

    device.enableDvrRecording = settings.dvrRecordingEnabled;
    device.dvrResolutionX = settings.dvrResolutionX;
    device.dvrResolutionY = settings.dvrResolutionY;
    device.dvrFps = settings.dvrFps;
    device.dvrBitrateBps = settings.dvrBitrateBps;
    device.dvrRetentionHours = settings.dvrRetentionHours;

    device.videoResolutionX = settings.streamResolutionX;
    device.videoResolutionY = settings.streamResolutionY;
    device.videoFps = settings.streamFps;
    device.videoBitrateBps = settings.streamBitrateBps;
}

/**
 * The changed keys of a fleet-defaults draft that can actually reach a truck.
 *
 * "No fleet-wide choice" is not a value a truck can follow: with no stated old value there are no
 * followers to move — a unit with nothing set reads as undefined, and so does one running a custom
 * tracker, which is a deliberate setting, not drift. And a change *to* no-choice writes nothing
 * (applyRadarVideo leaves unset presets alone). Either way the key must pick no push targets, or
 * the first fleet-wide preset choice claims every blank and custom unit in the fleet — which is
 * exactly the over-eager confirmation dialog this exists to prevent.
 */
export function pushableRadarVideoKeys(
    saved: RadarVideoSettings,
    draft: RadarVideoSettings,
): (keyof RadarVideoSettings)[] {
    const keys = Object.keys(saved) as (keyof RadarVideoSettings)[];
    return keys.filter((key) => draft[key] !== saved[key] && saved[key] !== undefined && draft[key] !== undefined);
}

/** The catalogue split the pickers offer — a radar tracker slot only accepts radar profiles. */
export const trackersOfType = (configs: TrackerConfig[], type: TrackerConfigType): TrackerConfig[] =>
    configs.filter((t) => t.type === type);

/**
 * The quality ladder, shared with the main dashboard's device editor.
 *
 * These used to be this console's own three-step lists with different numbers — "Medium" meant
 * 640×480 here and 1280×720 there, so a device saved on one screen read as unrecognisable custom
 * values on the other. Both recording and streaming now offer the one shared ladder; the fleet's
 * stated defaults are streaming Lowest and recording Medium off that ladder.
 */
const consoleProfile = (p: VideoQualityProfile) => ({
    label: `${p.name} · ${p.resolutionX}×${p.resolutionY} · ${p.fps} fps`,
    width: p.resolutionX,
    height: p.resolutionY,
    fps: p.fps,
    bitrateBps: p.bitrateBps,
});

export const VIDEO_PROFILES = VIDEO_QUALITY_PROFILES.map(consoleProfile);
export const STREAM_PROFILES = VIDEO_QUALITY_PROFILES.map(consoleProfile);

export const RETENTION_DAY_CHOICES = [7, 14, 30];

export function matchProfile<T extends { width: number; height: number; fps: number }>(
    profiles: T[],
    width: number,
    height: number,
    fps: number,
): T | undefined {
    return profiles.find((p) => p.width === width && p.height === height && p.fps === fps);
}
